// A bordered card of DetailRows with an optional section label (GET-266).
// The card has no inner padding: each row pads itself and draws its own
// hairline, and the group sets `last` on the final row so it has none.

import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTheme } from '../../styles/ThemeProvider';
import { useCommonStyles } from '../../styles/useCommonStyles';
import type { DetailRowProps } from './DetailRow';

export interface DetailGroupProps {
  title?: string;
  children: React.ReactNode;
  testID?: string;
}

export function DetailGroup({ title, children, testID }: DetailGroupProps) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        title: { marginBottom: theme.spacing.sm },
        card: {
          backgroundColor: theme.colors.backgroundSurface,
          borderWidth: theme.borders.borderWidth,
          borderColor: theme.borders.borderColor,
          borderRadius: theme.radius.radiusMd,
          overflow: 'hidden',
        },
      }),
    [theme]
  );

  // toArray drops null / false children, so conditionally rendered rows
  // don't stop the real final row from getting `last`.
  const rows = React.Children.toArray(children);
  const lastIndex = rows.length - 1;

  return (
    <View testID={testID}>
      {title ? <Text style={[commonStyles.sectionLabel, styles.title]}>{title}</Text> : null}
      <View style={styles.card}>
        {rows.map((child, index) =>
          index === lastIndex && React.isValidElement<DetailRowProps>(child)
            ? React.cloneElement(child, { last: true })
            : child
        )}
      </View>
    </View>
  );
}

export default DetailGroup;
